// src/services/questionsCache.js
// ---------------------------------------------------------------------------
// Fetch the questions before the guest gets to them.
//
// The activation id is known the moment the code is scanned. The questions were
// not requested until the guest reached the last screen, three taps later, so
// every guest paid for a fresh Firestore listener while looking at a spinner
// that said "Loading questions...". On a bar's wifi that is a few seconds of
// somebody holding a phone and wondering whether it is broken.
//
// This opens the listener as soon as the id is known and keeps it. By the time
// the guest has accepted the disclaimer, typed their name and read the
// promotion, the answer has been sitting in memory for a while and the screen
// renders with no spinner at all.
//
// It is the same single-equality-filter query the screen used, so it still needs
// no composite index and still cannot fail closed. The listener stays live, so
// an edit made in the portal during a pitch still lands on the phone.
//
// Everything here fails open. A guest must never be blocked by a cache.
// ---------------------------------------------------------------------------
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

/** The one live subscription, its last result, and who wants to know. */
let state = {
  eventId: null,
  rows: null, // null means "no answer yet", [] means "answered, none enabled"
  error: false,
  unsub: null,
  listeners: new Set(),
};

/**
 * Enabled questions in the order the guest should be asked them.
 *
 * `isEnabled !== false` rather than `=== true`, because a question written
 * before that field existed has neither. `sortOrder ?? order` for the same
 * reason. Both are applied here and not in the query, deliberately: a compound
 * query needs an index, and a missing index throws rather than returning empty.
 */
function shape(snapshot) {
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((q) => q.isEnabled !== false && q.questionText)
    .sort(
      (a, b) => (a.sortOrder ?? a.order ?? 0) - (b.sortOrder ?? b.order ?? 0),
    );
}

function publish() {
  state.listeners.forEach((fn) => {
    try {
      fn(state.rows, state.error);
    } catch (e) {
      // A throwing listener must not stop the others being told.
    }
  });
}

function stop() {
  if (state.unsub) {
    try {
      state.unsub();
    } catch (e) {
      /* already gone */
    }
  }
  state = { eventId: null, rows: null, error: false, unsub: null, listeners: state.listeners };
}

/**
 * Start listening for this activation's questions, if not already.
 *
 * Safe to call repeatedly and on every render. Call it the moment an activation
 * id is known, which is at the scan, not at the questionnaire.
 */
export function primeQuestions(eventId) {
  if (!eventId || state.eventId === eventId) return;

  // A different activation. Drop the old listener and its answer, or a guest
  // who scanned a second code would be shown the first one's questions.
  if (state.eventId) stop();

  state.eventId = eventId;
  state.rows = null;
  state.error = false;

  try {
    state.unsub = onSnapshot(
      query(collection(db, "eventQuestions"), where("eventId", "==", eventId)),
      (snapshot) => {
        state.rows = shape(snapshot);
        state.error = false;
        publish();
      },
      () => {
        // Fail open. The screen falls back to its own read, and if that fails
        // too the guest continues to the reward rather than being stranded.
        state.error = true;
        publish();
      },
    );
  } catch (e) {
    state.error = true;
  }
}

/**
 * Watch the questions for an activation.
 *
 * If the answer is already cached, `onRows` is called **synchronously**, before
 * this returns. That is the whole point: the questionnaire renders on its first
 * paint instead of after a round trip.
 *
 * @returns {function} unsubscribe
 */
export function subscribeQuestions(eventId, onRows) {
  primeQuestions(eventId);
  state.listeners.add(onRows);

  if (state.rows !== null || state.error) onRows(state.rows, state.error);

  return () => state.listeners.delete(onRows);
}

/** What is cached right now, or null if nothing has arrived yet. */
export function peekQuestions(eventId) {
  return state.eventId === eventId ? state.rows : null;
}

/** For the end of a visit. Not required, the listener is cheap and shared. */
export function resetQuestions() {
  stop();
}
